import * as React from 'react';
import _ from 'lodash';

import {
  ACTION_BUSINESS_CLICK_CHECKOUT_REVIEW,
  ACTION_CHECKOUT_ADDONS_SOLD_OUT_BLOCKS_CHECKOUT,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { Button, ChipLabel, ExpandableParagraph, Typography } from 'omakase';
import { Grid } from 'ui';
import { amplitudeSendEvent } from 'utils/analytics/amplitude';
import { core } from '@tock/coretypes';
import {
  generateOptionGroupProps,
  getTicketOptionDefaultSelection,
  helpTextFromTicketOptionGroup,
  isOptionGroupValid,
  isRequiredOptionGroup,
} from '@tock/shared/utils/option';
import { getButtonStateForOptionGroup } from '@tock/shared/utils/supplementsUtils';
import { getToGoCardData, getToGoCardGroupData } from '@tock/shared/utils/ToGoCard';
import { makeStyles } from '@material-ui/core/styles';
import { useAddonsPriceDisplayDetails } from 'utils/priceDisplayUtils';
import { useConsumerConversionAnalytics } from 'hooks/analytics/useConsumerConversionAnalytics';
import { useMountEffect } from '@tock/hooks';
import SupplementCompact from 'components/business/addons/SupplementCompact';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(5),
    '&:first-child': {
      marginTop: theme.spacing(3),
      [theme.breakpoints.up('sm')]: {
        '&': {
          marginTop: theme.spacing(4),
        },
      },
    },
  },
  headingSub: {
    alignItems: 'center',
    display: 'flex',
    flex: '1 0 auto',
    justifyContent: 'space-between',
  },
  name: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

interface Props {
  /**
   * used in supplement group compact, determines if items will be 2 per row on full screens or not
   */
  collapse?: boolean;
  /**
   * show all sections at once, or move section by section via action buttons
   */
  moveSectionBySection?: boolean;
  ticketType: core.ITicketType;
  ticketOptionGroup: core.ITicketOptionGroup;
  optionsWithModifiers: number[];
  modifierOptionGroups: core.ITicketOptionGroup[];
  ticketOptions: core.ITicketOption[];
  optionOrders: core.IOptionOrder[];
  partyOptionOrders?: core.IOptionOrder[];
  ticketOptionPrices: core.IPrice[];
  isOpen: boolean;
  partySize: number;
  isSkipped: boolean;
  isCompleted: boolean;
  onEdit(ticketOptionGroupId: number): void;
  onChangeQuantity(selectedOptions: core.IOptionOrder[]): void;
  onClearSelections(selectedOptions: core.IOptionOrder[], ticketOptionGroupId: number): void;
  onSaveChoices(ticketOptionGroupId: number): void;
  onOpenModifierModal(
    ticketOption: core.ITicketOption,
    ticketOptionGroup: core.ITicketOptionGroup,
    modifierOptionGroups: core.ITicketOptionGroup[],
    ticketOptionListIndex: number,
    defaultQuantity: number
  ): void;
  patron?: core.IPatron;
  onClickBack(): void;
  isBackEnabled: boolean;
  nextSectionInfo?: { id: number; name: string };
  totalPrice?: number;
}

export type SupplementGroupProps = Props;
export type GroupComponentType = 'COMPACT' | 'DEFAULT';

const SupplementGroupCompact: React.FunctionComponent<SupplementGroupProps> = ({
  collapse = true,
  isBackEnabled,
  modifierOptionGroups,
  moveSectionBySection = false,
  nextSectionInfo,
  onChangeQuantity,
  onClearSelections,
  onClickBack,
  onOpenModifierModal,
  onSaveChoices,
  optionOrders,
  patron,
  partyOptionOrders,
  partySize,
  ticketOptionGroup,
  ticketOptions,
  ticketType,
  ticketOptionPrices,
  totalPrice,
}) => {
  const classes = useStyles();
  const sendConversionAnalytics = useConsumerConversionAnalytics();
  const { numSelectedInGroup } = generateOptionGroupProps({
    patron,
    optionOrders,
    ticketOptions,
    onChangeQuantity,
    ticketOptionGroup,
  });

  const handleOpenModifierModal = (
    ticketOptionId: number,
    modifierOptionGroups: core.ITicketOptionGroup[],
    ticketOptionListIndex: number
  ) => {
    const ticketOption = _.find(ticketOptions, (option) => option.id === ticketOptionId);
    if (!ticketOption) {
      return;
    }
    const defaultQuantity = getTicketOptionDefaultSelection({
      ticketOption,
      ticketOptionGroup,
      ticketOptionsInGroup: ticketOptions,
      partySize,
      optionOrders,
      partyOptionOrders,
      optionExclusion: ticketType.optionExclusion ?? [],
    });
    onOpenModifierModal(
      ticketOption,
      ticketOptionGroup,
      modifierOptionGroups,
      ticketOptionListIndex,
      defaultQuantity
    );
  };

  const { sortedTicketOptions, isOptionGroupMaxed } = getToGoCardGroupData({
    optionOrders,
    partyOptionOrders,
    ticketOptions,
    ticketOptionGroup,
    partySize,
  });

  const helpText = getHelpText({ partySize, ticketOptionGroup, isOptionGroupMaxed });

  const isOptionGroupInvalid = !isOptionGroupValid(
    optionOrders,
    sortedTicketOptions,
    partySize,
    ticketOptionGroup
  );

  let buttonText;
  if (nextSectionInfo !== undefined) {
    buttonText = `Next: ${nextSectionInfo.name}`;
  } else if (totalPrice !== undefined && totalPrice > 0) {
    buttonText = 'Next: Review and purchase';
  } else {
    buttonText = 'Next: Review';
  }
  const buttonInfo = getButtonStateForOptionGroup(
    ticketOptionGroup as core.TicketOptionGroup,
    isOptionGroupInvalid,
    numSelectedInGroup,
    partySize,
    buttonText,
    false
  );
  useMountEffect(() => {
    if (
      buttonInfo.disabled &&
      ticketOptionGroup.isRequired &&
      ticketOptionGroup.ticketOption?.every(
        (option) => typeof option.currentQuantity === 'number' && option.currentQuantity < partySize
      )
    ) {
      amplitudeSendEvent(ACTION_CHECKOUT_ADDONS_SOLD_OUT_BLOCKS_CHECKOUT, {
        productName: ticketOptionGroup.name ?? undefined,
      });
    }
  });

  const { useInclusivePricing, feeDisclaimerLabel, showFeeDisclaimerLabel } =
    useAddonsPriceDisplayDetails(ticketType);

  const supplements = _.map(sortedTicketOptions, (ticketOption, index) => {
    const { price, numSelectedOfSupplement, isSoldOut, isOptionMaxed, isExcluded } =
      getToGoCardData({
        ticketOption,
        ticketOptionGroup,
        ticketOptions,
        ticketOptionPrices,
        optionOrders,
        optionExclusion: ticketType.optionExclusion,
        useInclusivePricing,
      });
    const shouldDisplayLimitReached = isExcluded || isOptionGroupMaxed || isOptionMaxed;

    return (
      <SupplementCompact
        collapse={collapse}
        description={ticketOption.description ?? ''}
        disabled={isSoldOut || shouldDisplayLimitReached}
        isLimitReached={shouldDisplayLimitReached}
        isSoldOut={isSoldOut}
        modifierOptionGroups={_.filter(
          modifierOptionGroups,
          (optionGroup) => optionGroup.parentTicketOptionId === ticketOption.id
        )}
        id={ticketOption.id as number}
        key={ticketOption.id as number}
        name={ticketOption.name ?? ''}
        image={ticketOption.image ?? undefined}
        price={price}
        quantity={numSelectedOfSupplement}
        onOpenModifierModal={(ticketOptionId: number, modifierGroups: core.ITicketOptionGroup[]) =>
          handleOpenModifierModal(ticketOptionId, modifierGroups, index)
        }
      />
    );
  });

  return (
    <div className={classes.root}>
      <div>
        <Typography component="h2" variant="body01Strong" mb={0.5}>
          {ticketOptionGroup.name}
        </Typography>
        {ticketOptionGroup.description && (
          <ExpandableParagraph
            paragraphText={ticketOptionGroup.description}
            linkify={true}
            cutoffLength={255}
            mb={1}
          />
        )}
        <Typography
          className={classes.headingSub}
          variant="body02"
          color="fgSubtle"
          component="div"
          mb={1.5}
        >
          <p>{!!helpText && helpText}</p>
          {!!isRequiredOptionGroup(ticketOptionGroup) && (
            <ChipLabel variant="warning">Required</ChipLabel>
          )}
        </Typography>

        <Grid container spacing={4}>
          <Grid item container spacing={2} data-testid="supplementGroup-supplements">
            {supplements}
          </Grid>

          {showFeeDisclaimerLabel && (
            <Grid item container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="fgSubtle">
                  {feeDisclaimerLabel}
                </Typography>
              </Grid>
            </Grid>
          )}

          {moveSectionBySection && (
            <Grid item container spacing={2} justify="flex-end">
              <Grid item xs={12} md={6}>
                <Button
                  fullWidth
                  disabled={!isBackEnabled}
                  variant="basic"
                  size="large"
                  onClick={onClickBack}
                  data-testid="back-button"
                >
                  Back
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <Button
                  disabled={buttonInfo.disabled}
                  fullWidth
                  variant="primary"
                  size="large"
                  onClick={() => {
                    onSaveChoices(ticketOptionGroup.id as number);
                    sendConversionAnalytics(ACTION_BUSINESS_CLICK_CHECKOUT_REVIEW);
                  }}
                  data-testid="supplement-group-confirm-button"
                >
                  {_.truncate(buttonInfo.text, { length: 31, separator: '...' })}
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </div>
    </div>
  );
};

const getHelpText = ({
  partySize,
  ticketOptionGroup,
  isOptionGroupMaxed,
}: {
  partySize: number;
  ticketOptionGroup: core.ITicketOptionGroup;
  isOptionGroupMaxed: boolean;
}) => {
  const optionGroupHelpText = helpTextFromTicketOptionGroup(partySize, ticketOptionGroup);

  if (optionGroupHelpText === undefined || optionGroupHelpText.length === 0) {
    return isOptionGroupMaxed ? 'Limit reached' : optionGroupHelpText;
  }

  return isOptionGroupMaxed
    ? (optionGroupHelpText as string) + ' - Limit reached'
    : optionGroupHelpText;
};

export default SupplementGroupCompact;
